// Phase 15 · Portal Screen 0 — Home. A distributor's equivalent of
// MobileHome.jsx: a welcome header, a live "today" dashboard, and quick
// links into Distributor Stock / Today's Activity / My Day Summary — same
// shape as the SE home screen, built on the distributor's own data (orders
// + visits) rather than SE's attendance/PJP-based metrics, which don't
// apply here (a distributor doesn't punch attendance or get a PJP).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ClipboardList, Phone, ShoppingCart, Scale, BarChart3, PackageMinus, MessagesSquare, Target } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function todayIso() {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

export default function PortalHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const date = todayIso();
    setLoading(true);
    Promise.all([
      api.get("/visits", { params: { user_id: user.id, date_from: date, date_to: date, page_size: 100 } }),
      // channel=secondary — this dashboard is "today's beat work" (calls +
      // the orders booked on those calls); without it, a distributor's own
      // primary order (distributor → company, a separate channel entirely)
      // got summed in too, inflating kg/value/order-count past what
      // Today's Activity (which excludes primary orders) shows for the
      // same day.
      api.get("/orders", { params: { user_id: user.id, date_from: date, date_to: date, page_size: 100, enrich: "grid", channel: "secondary" } }),
    ]).then(([visits, orders]) => {
      const v = visits.data.items || [];
      const o = orders.data.items || [];
      // "Calls" counts finished visits only — one still checked-in
      // (status "in_progress") isn't done yet, same rule as Today's Activity.
      const tc = v.filter((r) => r.status !== "in_progress").length;
      const pc = v.filter((r) => r.status === "completed" && r.outcome === "productive").length;
      const kg = o.reduce((s, r) => s + (Number(r.total_qty_kg) || 0), 0);
      setStats({ tc, pc, orders: o.length, kg });
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [user.id]);

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="portal-home-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-400">Welcome</div>
        <div className="text-2xl font-extrabold tracking-tight text-neutral-900 leading-tight">
          Hi, <span className="tulsi-text-gradient">{(user?.name || "").split(" ")[0] || "there"}</span>
        </div>
        <div className="text-sm text-neutral-500 mt-1">Here's how today looks.</div>
      </div>

      <Card className="border-neutral-200 rounded-2xl overflow-hidden" data-testid="portal-home-dashboard">
        <CardContent className="p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-neutral-400">Today's dashboard</div>
          {loading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-16 bg-neutral-100 rounded-xl" />
              <div className="h-16 bg-neutral-100 rounded-xl" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <StatTile icon={Phone} label="Calls" value={stats?.tc ?? 0} testid="portal-home-tc" />
                <StatTile icon={ShoppingCart} label="Orders" value={stats?.orders ?? 0} testid="portal-home-orders" />
                <StatTile icon={Scale} label="KG" value={(stats?.kg ?? 0).toFixed(1)} testid="portal-home-kg" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" onClick={() => nav("/today-activity")}
              data-testid="portal-home-activity-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <ShoppingBag className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">Today's Activity</span>
        <span className="text-xs text-neutral-400">Visits · Orders</span>
      </Button>

      <Button variant="outline" onClick={() => nav("/portal/day-summary")}
              data-testid="portal-home-day-summary-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <ClipboardList className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">My Day Summary</span>
        <span className="text-xs text-neutral-400">Past days</span>
      </Button>

      <Button variant="outline" onClick={() => nav("/portal/sales-report")}
              data-testid="portal-home-sales-report-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <BarChart3 className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">Sales Report</span>
        <span className="text-xs text-neutral-400">Beat · Retailer</span>
      </Button>

      <Button variant="outline" onClick={() => nav("/portal/targets")}
              data-testid="portal-home-targets-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <Target className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">My Target</span>
        <span className="text-xs text-neutral-400">Target vs achievement (kg)</span>
      </Button>

      <Button variant="outline" onClick={() => nav("/portal/primary-sales-return")}
              data-testid="portal-home-primary-sr-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <PackageMinus className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">Primary Sales Return</span>
        <span className="text-xs text-neutral-400">Return stock to company</span>
      </Button>

      <Button variant="outline" onClick={() => nav("/portal/queries")}
              data-testid="portal-home-queries-cta"
              className="h-14 rounded-xl border-neutral-200 font-medium w-full justify-start px-5">
        <MessagesSquare className="h-4 w-4 mr-3 text-[#6D2C91]" />
        <span className="flex-1 text-left">Query Tracker</span>
        <span className="text-xs text-neutral-400">Ledger, transport, damage, claims</span>
      </Button>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 px-2.5 py-2" data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-neutral-400">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-lg font-extrabold text-neutral-900 mt-0.5">{value}</div>
    </div>
  );
}
